// ゲームのページ。3つの状態を持つ。並びはどれも同じ：パンくず・ゲームの枠・枠の下の帯（左の列）と、
// 広告の枠・ランキング・次に遊ぶ（右の列）。CrazyGames の並べ方。
//   遊ぶ前：会社の LP と同じピンクの方眼の地に、遊んでいる様子の動画の札・題名・ひとこと・札・大きな「遊ぶ」。
//           枠の下の帯は、題名と Info・音・全画面。一覧を読むまでは、形だけの骨組みを出す。
//   PC は「遊ぶ前」を出さず、ページに入ったらすぐゲームを読み込む（読み込みの間は遊ぶ前と同じ形の画面に、読み込みの進み）。
//           「もどる」は来たページへ戻る。スマホと指で触る端末は、遊ぶ前の画面の「遊ぶ」で始める。
//   遊んでいる最中：PC では、枠の中身だけをゲームに替える（上の帯・左のメニュー・右の列＝広告の枠は出たまま。
//           2026-10-25 ちさとさん「プレイ中も枠の中で基本は遊ぶようにしたい。広告を入れた時にプレイ中も常に表示されているようにしたい」）。
//           枠の下の帯に、もどる・保存の知らせ・スタンプ・部屋の人・音・全画面を寄せる。
//           スマホ（左のメニューが出ない幅）と指で触る端末は、今までどおり画面いっぱいで遊ぶ（帯は上へ。style.css が決める）。
//           ゲームは別の住所の iframe（sandbox つき）で動かす。
//   終わった後：点・順位・もう一回・次のゲーム・終わった後の一言。ログインの促しはここで1回だけ。
// 枠と枠の下の帯の下には、どの状態でも説明の欄（題名の h1・このゲームについて・遊び方・コツ）を出す（CrazyGames のゲームのページと同じ位置）。
//   PC は左の大きい列（ランキングの列の左）、スマホはゲームの下に続ける。スマホで画面いっぱいに遊んでいる間は隠す（style.css）。
//   ページの h1 はこの欄の題名だけ（枠の中の題名と、枠の下の帯の題名は見出しにしない）。
import { STAMPS, type LeaderboardEntry, type Period, type ResultParams, type StampId, type SubmitResult } from "@appful/sdk/protocol";
import { track } from "../analytics";
import { onSession, session, userInfo } from "../auth";
import { GameBridge } from "../bridge";
import { loadCatalog, type CatalogBoard, type CatalogGame } from "../catalog";
import { config } from "../config";
import { getLocale, localePath, pick, t, tEn, type MessageKey } from "../i18n";
import * as leaderboard from "../leaderboard";
import { isMuted, markPlayed, setMuted, updateLocalBest } from "../local";
import { resetSaves } from "../saves";
import type { RoomPlayer } from "../online";
import { block, report, REPORT_REASONS, type ReportReason } from "../safety";
import { openDialog, openSignIn, toast } from "./dialogs";
import { h } from "./dom";
import { icon } from "./icons";
import { adSlot, gamePath, gameTile, tags } from "./parts";
import type { View } from "./view";

// ゲームに許すこと。外への画面の移動・ポップアップ・フォームの送信は許さない。
const SANDBOX = "allow-scripts allow-same-origin allow-pointer-lock";
const ALLOW = "fullscreen; gamepad; autoplay";
const NUDGE_KEY = "appful:nudge-dismissed";

const fmt = (n: number) => n.toLocaleString(getLocale());

/** ランキングの名前。名前を決めていない人の後ろの「#4桁」は、小さく薄く出す（名前に「#」は使えないので、末尾の #4桁 は番号） */
function rankName(name: string): (string | HTMLElement)[] {
  const m = /^(.+)(#\d{4})$/.exec(name);
  return m ? [m[1]!, h("span", { class: "rk-tag" }, m[2]!)] : [name];
}

function rankRows(entries: LeaderboardEntry[]): HTMLElement[] {
  return entries.map((e) =>
    h(
      "li",
      { class: e.isMe ? "me" : "" },
      h("span", { class: "rk-n" }, String(e.rank)),
      h("span", { class: "rk-name" }, ...rankName(e.displayName)),
      h("span", { class: "rk-s nw" }, fmt(e.score)),
    ),
  );
}

/** ランキングのカード（遊ぶ前の横）。今日とこれまでを切り替えられる */
function rankingCard(game: CatalogGame) {
  const board = game.leaderboards[0];
  const el = h("div", { class: "card rk" });
  if (!board) return { el: null, refresh() {}, destroy() {} };
  let period: Period = board.periods[0] ?? "all";
  const title = h("h2", { class: "h3" });
  const tabs = h("div", { class: "rk-tabs", role: "tablist" });
  const list = h("ol", { class: "rk-list", "data-testid": "ranking-list" });
  let seq = 0;

  async function refresh() {
    const my = ++seq;
    title.replaceChildren(icon("trophy"), t(period === "daily" ? "rank.daily" : "rank.all"));
    tabs.replaceChildren(
      ...(board!.periods.length > 1
        ? board!.periods.map((p) =>
            h(
              "button",
              {
                type: "button",
                role: "tab",
                "aria-selected": String(p === period),
                class: p === period ? "chip on" : "chip",
                onclick: () => {
                  period = p;
                  void refresh();
                },
              },
              t(p === "daily" ? "rank.tab.daily" : "rank.tab.all"),
            ),
          )
        : []),
    );
    try {
      const entries = await leaderboard.top(game.id, board!, period, 5);
      if (my !== seq) return;
      list.replaceChildren(...(entries.length ? rankRows(entries) : [h("li", { class: "empty small" }, t("rank.empty"))]));
    } catch (e) {
      console.error(e);
      if (my === seq) list.replaceChildren(h("li", { class: "empty small" }, t("rank.error")));
    }
  }

  el.append(title, tabs, list);
  const offs = [leaderboard.onScoreSubmitted((gid) => gid === game.id && void refresh()), onSession(() => void refresh())];
  void refresh();
  return { el, refresh, destroy: () => offs.forEach((f) => f()) };
}

const stampText = (id: StampId) => t(`stamp.${id}`);

/** 部屋の人の窓。自分以外に「ブロック」と「通報」を出す */
function openPlayers(gameId: string, players: RoomPlayer[], me: number) {
  void openDialog((close) => {
    const body = h("div", {});
    const list = () =>
      body.replaceChildren(
        h("h2", { class: "h1 ph" }, t("online.players")),
        h(
          "ul",
          { class: "op-list" },
          ...players.map((p) =>
            h(
              "li",
              { class: p.connected ? "" : "gone" },
              h("span", { class: "op-name ph" }, p.displayName, p.seat === me && h("small", {}, `（${t("online.you")}）`), !p.connected && h("small", {}, `（${t("online.left")}）`)),
              p.seat !== me &&
                h(
                  "span",
                  { class: "op-act" },
                  h("button", { class: "btn-text", type: "button", "data-testid": `block-${p.seat}`, onclick: () => void doBlock(p) }, t("online.block")),
                  h("button", { class: "btn-text", type: "button", "data-testid": `report-${p.seat}`, onclick: () => reasons(p) }, t("online.report")),
                ),
            ),
          ),
        ),
        h("p", { class: "small ph" }, t("online.blockNote")),
      );
    const doBlock = async (p: RoomPlayer) => {
      try {
        await block(p.uid);
        toast(t("online.blocked", { name: p.displayName }));
        close();
      } catch (e) {
        console.error(e);
        toast(t("error.generic"));
      }
    };
    const reasons = (p: RoomPlayer) =>
      body.replaceChildren(
        h("h2", { class: "h3 ph" }, t("online.reportTitle", { name: p.displayName })),
        h("p", { class: "ph" }, t("online.reportReason")),
        h(
          "div",
          { class: "op-reasons" },
          ...REPORT_REASONS.map((r: ReportReason) =>
            h(
              "button",
              {
                class: "btn-sub",
                type: "button",
                "data-testid": `reason-${r}`,
                onclick: async () => {
                  try {
                    await report(p.uid, gameId, r);
                    toast(t("online.reported"));
                    close();
                  } catch (e) {
                    console.error(e);
                    toast(t("error.generic"));
                  }
                },
              },
              t(`report.${r}`),
            ),
          ),
        ),
        h("p", { class: "center" }, h("button", { class: "btn-text", type: "button", onclick: list }, t("online.back"))),
      );
    list();
    return [body];
  }, t("online.players"), "op-dialog");
}

/** 段落に分ける（空行で区切る。tools/lib/site-pages.mjs の paragraphs と同じ分け方） */
const paragraphs = (s: string | undefined) =>
  (s ?? "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

/** 説明の欄の見出し：一覧の見出しと同じ、LP の ABOUT の丸くふくらんだ英字に、日本語を小さく添える（英語の画面では英字だけ） */
function aboutHeading(key: MessageKey): HTMLElement {
  const ja = getLocale() !== "en";
  return h("h2", { class: "h2 gd-h" }, h("span", { class: "mk", "aria-hidden": ja ? "true" : undefined }, tEn(key)), ja && h("span", { class: "h2-j" }, t(key)));
}

/**
 * 説明の欄（だれでも読める文）：題名（h1）・このゲームについて（description を先頭の段落にし、about の段落を続ける）・遊び方・コツ（tips の箇条書き）。
 * about と tips が無いゲームは、description と遊び方だけで出す。検索と AdSense の見回りが JS なしで読めるように、
 * 静的なページ（tools/build.mjs → tools/lib/site-pages.mjs の gameBody）にも同じ中身を入れている
 */
function aboutSection(game: CatalogGame): HTMLElement {
  const about = [pick(game.description), ...paragraphs(game.about && pick(game.about))].filter(Boolean);
  const tips = (game.tips ?? []).map((x) => pick(x)).filter(Boolean);
  return h(
    "section",
    { class: "gd", "data-testid": "game-about" },
    h("h1", { class: "gd-t ph" }, pick(game.title)),
    h("section", { class: "gd-sec" }, aboutHeading("play.info"), ...about.map((p) => h("p", {}, p))),
    h("section", { class: "gd-sec" }, aboutHeading("play.howTo"), ...paragraphs(pick(game.howToPlay)).map((p) => h("p", {}, p))),
    tips.length > 0 && h("section", { class: "gd-sec" }, aboutHeading("play.tips"), h("ul", { class: "gd-tips" }, ...tips.map((x) => h("li", {}, x)))),
  );
}

/** このゲームについて（Info の窓）：題名・札・説明・遊び方・保存の注意。スマホでは下から出る */
function openInfo(game: CatalogGame) {
  void openDialog(
    () => [
      h("div", { class: "info-hd" }, h("img", { class: "info-ic", src: game.thumbnail, alt: "", width: 512, height: 512 }), h("h2", { class: "info-t ph", tabindex: "-1", autofocus: "" }, pick(game.title))),
      tags(game),
      h("p", { class: "lead ph" }, pick(game.description)),
      h("h3", { class: "h3" }, t("play.howTo")),
      h("p", { class: "howto ph" }, pick(game.howToPlay)),
      h("p", { class: "small ph" }, t("play.saveNote")),
    ],
    t("play.info"),
    "info-dialog",
  );
}

/** 対戦のお休みの知らせ。日付は期限の日（見る人の言語と時刻で出す） */
function openSuspended(until: Date) {
  const date = until.toLocaleDateString(getLocale(), { month: "long", day: "numeric" });
  void openDialog(
    (close) => [
      h("h2", { class: "h3 ph" }, t("online.suspendedTitle")),
      h("p", { class: "ph", "data-testid": "suspended-note" }, t("online.suspended", { date })),
      h("p", { class: "center" }, h("a", { class: "btn-sub", href: "/legal/contact/", target: "_self" }, t("footer.contact"))),
      h("p", { class: "center" }, h("button", { class: "btn-text", type: "button", onclick: close }, t("common.close"))),
    ],
    t("online.suspendedTitle"),
  );
}

/** 読み込みの画面の時間の決まり：長くかかるときは言葉を変え、すぐ読み終わっても最低 0.8 秒は見せる（ちらつき防止）。
 *  読み終わりを知らせないゲームでも、20 秒で消してゲームを見せる */
function loadingTimers(el: HTMLElement, status: HTMLElement): { el: HTMLElement; done(): void } {
  const timer = window.setTimeout(() => (status.textContent = t("play.loadingMore")), 4000);
  const shownAt = performance.now();
  let finished = false;
  const done = () => {
    if (finished) return;
    finished = true;
    clearTimeout(timer);
    clearTimeout(fallback);
    window.setTimeout(() => {
      el.classList.add("done");
      window.setTimeout(() => el.remove(), 350);
    }, Math.max(0, 800 - (performance.now() - shownAt)));
  };
  const fallback = window.setTimeout(done, 20_000);
  return { el, done };
}

/** 読み込みの画面（スマホ・指で触る端末で、遊ぶを押してから、ゲームが「読み終わった」と知らせるまで）。うすいピンクの地に、
 *  跳ねる絵と影・題名・しましまの帯・一字ずつ跳ねる丸い英字の LOADING（LP の見出しと同じ字） */
function loadingScreen(game: CatalogGame): { el: HTMLElement; done(): void } {
  const status = h("p", { class: "ld-s" }, t("play.loading"));
  const el = h(
    "div",
    { class: "frame-loading", "aria-live": "polite" },
    h("img", { class: "ld-bg", src: game.thumbnail, alt: "" }),
    h(
      "div",
      { class: "ld-in" },
      h("span", { class: "ld-jump", "aria-hidden": "true" }, h("img", { class: "ld-art", src: game.thumbnail, alt: "", width: 512, height: 512 }), h("span", { class: "ld-sh" })),
      h("p", { class: "ld-t ph" }, pick(game.title)),
      h("span", { class: "ld-bar" }, h("span", {})),
      h("span", { class: "ld-mk", "aria-hidden": "true" }, ..."LOADING".split("").map((c) => h("i", {}, c))),
      status,
    ),
  );
  return loadingTimers(el, status);
}

/** 読み込みの画面（PC：ゲームのページに入ったらすぐ読み込む間）。遊ぶ前の画面と同じ形（方眼の地・動画の札・題名・ひとこと・札）で、
 *  「遊ぶ」のボタンの代わりに、しましまの帯で読み込みの進みを出す。読み終わるとゲームのスタート画面に替わる
 *  （2026-10-25 ちさとさん「ゲーム画面に入ったらすぐローディングして２枚目のスタート画面をなくしてほしい」。手本は CrazyGames） */
function stageLoading(game: CatalogGame): { el: HTMLElement; done(): void } {
  const status = h("p", { class: "ld-s", "aria-live": "polite" }, t("play.loading"));
  const el = h(
    "div",
    { class: "frame-loading stage-loading", "data-testid": "stage-loading" },
    h(
      "div",
      { class: "pre-in" },
      stageCard(game),
      h(
        "div",
        { class: "pre-tx" },
        h("p", { class: "pre-t ph" }, pick(game.title)),
        h("p", { class: "pre-hook ph" }, pick(game.hook)),
        tags(game),
        h("span", { class: "ld-bar" }, h("span", {})),
        status,
      ),
    ),
  );
  return loadingTimers(el, status);
}

/** 一覧を読むまでの骨組み（ゲームのページの形だけ） */
function gameSkeleton(): HTMLElement {
  const bone = (cls: string) => h("span", { class: `bone ${cls}` });
  return h(
    "div",
    { class: "wrap gm", "aria-busy": "true" },
    h("div", { class: "gm-main" }, bone("bone-crumb"), bone("bone-frame"), bone("bone-bar")),
    h("aside", { class: "side" }, bone("bone-ad"), h("div", { class: "nx-grid" }, ...Array.from({ length: 4 }, () => bone("gt")))),
  );
}

const reduceMotion = matchMedia("(prefers-reduced-motion: reduce)");

/** 遊ぶ前の枠の中の札：遊んでいる様子の動画（音なし・くり返し。一覧で指を乗せると流れる物と同じ）。読み込むまではサムネイル。
 *  動画の無いゲーム・動きを減らす設定・データを節約する設定では、サムネイルの静止画にする */
function previewArt(game: CatalogGame): HTMLElement {
  const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
  if (!game.preview || reduceMotion.matches || saveData) return h("img", { class: "pre-art", src: game.thumbnail, alt: "", width: 512, height: 512 });
  const v = h("video", {
    class: "pre-art",
    src: game.preview,
    poster: game.thumbnail,
    autoplay: true,
    muted: true,
    loop: true,
    playsinline: true,
    preload: "auto",
    disablepictureinpicture: true,
    disableremoteplayback: true,
    width: 512,
    height: 512,
    "aria-hidden": "true",
  });
  v.muted = true;
  // 自動で流せない端末（省電力の設定など）では、サムネイル（poster）のまま
  v.play().catch(() => undefined);
  return v;
}

/** 動画の札と、まわりのきらり（遊ぶ前の画面と、PC の読み込みの画面で使う） */
function stageCard(game: CatalogGame): HTMLElement {
  const spark = (n: number) => h("span", { class: `spk s${n}`, "aria-hidden": "true" });
  return h("div", { class: "pre-card" }, previewArt(game), spark(1), spark(2), spark(3));
}

/** 画面いっぱいで遊ぶ端末：スマホの幅と、指で触る端末（style.css の同じ条件の @media と合わせる）。
 *  それ以外（PC）は、ゲームのページに入ったらすぐ、枠の中でゲームを読み込む */
const FULLSCREEN_PLAY = matchMedia("(max-width: 48em), (pointer: coarse)");

export function gameView(id: string, navigate: (p: string) => void): View {
  const el = h("div", { class: "game-view" }, gameSkeleton());
  let cleanup: (() => void)[] = [];
  let onLocale: (() => void) | undefined;

  loadCatalog()
    .then((games) => {
      const game = games.find((g) => g.id === id);
      if (!game) {
        document.title = t("play.notFound");
        el.replaceChildren(h("div", { class: "wrap narrow" }, h("h1", { class: "h1" }, t("play.notFound")), h("a", { class: "btn-sub", href: localePath("/") }, t("notFound.back"))));
        return;
      }
      const others = games.filter((g) => g.id !== game.id);
      const next = others[0];
      let bridge: GameBridge | null = null;
      // ランキングのカードは、右の列を作るとき（renderSide）に作る
      let ranking: ReturnType<typeof rankingCard> = { el: null, refresh() {}, destroy() {} };
      let lastUid = session().user?.uid ?? null;
      // 遊んでいる最中に言語を変えたとき、帯の文言を付け直す（PC は上の帯の言語の切り替えが、遊んでいる間も見えている）
      let relabel: (() => void) | null = null;
      const [arW, arH] = game.aspectRatio.split(":").map(Number);
      // 枠の大きさの手がかり（style.css）：縦長のゲームは画面の高さまで、横長のゲームはゲームの縦横比で
      const stage = { class: `o-${game.orientation}`, style: game.orientation === "landscape" ? `--arw:${arW || 16};--arh:${arH || 9}` : undefined };
      const pageTitle = () => `${pick(game.title)}｜appful games`;
      const crumb = () => h("p", { class: "crumb" }, h("a", { href: localePath("/") }, t("nav.home")), " › ", h("span", {}, pick(game.title)));

      /** 右の列：広告の枠・ランキング・次に遊ぶ。遊んでいる最中も出したまま（作り直すと広告を読み直すので、言語を変えたときだけ作り直す） */
      const renderSide = () => {
        ranking.destroy();
        ranking = rankingCard(game);
        return h(
          "aside",
          { class: "side" },
          adSlot("play"),
          ranking.el,
          others.length > 0 && h("div", { class: "nx" }, h("h2", { class: "h3" }, t("play.next")), h("div", { class: "nx-grid" }, ...others.slice(0, 8).map((g) => gameTile(g)))),
        );
      };

      /** 遊ぶ前の左の列：パンくず・枠（動画の札・題名・ひとこと・札・遊ぶ）・枠の下の帯 */
      const preMain = (): HTMLElement[] => {
        const playBtn = h("button", { class: "btn-play", type: "button", "data-testid": "play-button", onclick: () => startPlaying() }, icon("play"), h("span", {}, t("play.play")));
        const orient = game.orientation === "landscape" ? t("play.landscape") : game.orientation === "portrait" ? t("play.portrait") : "";
        const sound = soundButton();
        return [
          crumb(),
          h(
            "div",
            { class: `frame ${stage.class}`, style: stage.style },
            h(
              "div",
              { class: "pre-in" },
              stageCard(game),
              h(
                "div",
                { class: "pre-tx" },
                // ページの見出し（h1）は下の説明の欄の題名。ここは見出しにしない
                h("p", { class: "pre-t ph" }, pick(game.title)),
                h("p", { class: "pre-hook ph" }, pick(game.hook)),
                tags(game),
                playBtn,
                h("p", { class: "pre-note" }, h("span", { class: "nw" }, t("play.noSignup")), orient && "・", orient && h("span", { class: "nw" }, orient)),
              ),
            ),
          ),
          // 枠の下の帯（CrazyGames と同じ位置）：小さな絵と題名、右に Info・音・全画面。
          // 説明と遊び方は Info の中に入れる（2026-10-24 ちさとさん「説明セクションは音量ボタンの左側に Info ボタンを用意してその中に収めて」）
          h(
            "div",
            { class: "gbar" },
            h("img", { class: "gbar-ic", src: game.thumbnail, alt: "", width: 512, height: 512 }),
            h("span", { class: "gbar-t ph" }, pick(game.title)),
            h(
              "span",
              { class: "gbar-r" },
              h(
                "button",
                { class: "ib", type: "button", "aria-label": t("play.info"), title: t("play.info"), "aria-haspopup": "dialog", "data-testid": "info-button", onclick: () => openInfo(game) },
                icon("info"),
              ),
              sound.el,
              // 全画面は、できる端末だけ（iPhone の Safari は要素の全画面ができない）。押すと全画面で遊び始める
              document.fullscreenEnabled &&
                h("button", { class: "ib", type: "button", "aria-label": t("play.fullscreen"), title: t("play.fullscreen"), onclick: () => startPlaying(true) }, icon("full")),
            ),
          ),
        ];
      };

      // ── ページ全体 ──
      // PC は遊ぶ前の画面を出さず、すぐゲームを読み込む（ゲームの中のスタート画面だけが出る）。
      // スマホと指で触る端末は、画面いっぱいにするのに 1 回押してもらうので、遊ぶ前の画面を出す
      let main: HTMLElement;
      let about: HTMLElement;
      let side: HTMLElement;
      const renderPage = () => {
        document.body.classList.remove("playing");
        document.title = pageTitle();
        const auto = !FULLSCREEN_PLAY.matches;
        main = h("div", { class: "gm-main pstage" }, ...(auto ? [] : preMain()));
        about = aboutSection(game);
        side = renderSide();
        // 左の列：ゲームの枠と枠の下の帯（main）・その下に説明の欄。右の列：広告の枠・ランキング・次に遊ぶ
        el.replaceChildren(h("div", { class: "wrap gm" }, h("div", { class: "gm-col" }, main, about), side));
        if (auto) startPlaying();
      };

      /** 音のボタン（遊ぶ前の帯と、遊んでいる最中の帯で使う） */
      function soundButton() {
        const el = h("button", { class: "ib", type: "button" });
        const render = () => {
          el.replaceChildren(icon(isMuted() ? "mute" : "sound"));
          el.setAttribute("aria-label", isMuted() ? t("play.soundOn") : t("play.soundOff"));
          el.title = el.getAttribute("aria-label")!;
        };
        el.addEventListener("click", () => {
          setMuted(!isMuted());
          render();
          bridge?.send("sound", { muted: isMuted() });
        });
        render();
        return { el, render };
      }

      // ── 遊んでいる最中 ──
      function startPlaying(fullscreen = false) {
        // PC は枠の中で遊ぶ（読み込みの間は遊ぶ前の画面と同じ形）。スマホと指で触る端末は画面いっぱい
        const inFrame = !FULLSCREEN_PLAY.matches;
        document.body.classList.add("playing");
        markPlayed(game!.id);
        track("game_open", { game_id: game!.id, version: game!.version });
        const loading = inFrame ? stageLoading(game!) : loadingScreen(game!);
        const frame = h("iframe", {
          src: `${config.playOrigin}${game!.entry}`,
          title: pick(game!.title),
          sandbox: SANDBOX,
          allow: ALLOW,
          referrerpolicy: "no-referrer",
          "data-testid": "game-frame",
          style: `--ar:${arW || 9}/${arH || 16}`,
        });
        // 枠は遊ぶ前と同じ大きさ。向きの印で、端末（枠）の向きとゲームの向きが同じなら枠いっぱいに広げる（style.css）
        const pframe = h("div", { class: `pframe ${stage.class}`, style: stage.style }, frame, loading.el);
        const saved = h("span", { class: "saved", hidden: true });
        const sound = soundButton();
        // ── 対戦の部屋にいる間だけ出す：スタンプと部屋の人 ──
        let roomPlayers: RoomPlayer[] = [];
        let roomMe = -1;
        const stampBtns = STAMPS.map((id) =>
          h(
            "button",
            {
              class: "stamp",
              type: "button",
              "data-testid": `stamp-${id}`,
              onclick: () => {
                bridge?.sendStamp(id);
                showStamp(t("online.you"), id);
                stampPanel.hidden = true;
              },
            },
            stampText(id),
          ),
        );
        const stampPanel = h("div", { class: "stamps", hidden: true, "data-testid": "stamp-panel" }, ...stampBtns);
        const stampBtn = h("button", { class: "pb-btn", type: "button", hidden: true, "data-testid": "stamp-button", onclick: () => (stampPanel.hidden = !stampPanel.hidden) }, t("online.stamp"));
        const playersBtn = h(
          "button",
          { class: "pb-btn", type: "button", hidden: true, "data-testid": "players-button", onclick: () => openPlayers(game!.id, roomPlayers, roomMe) },
          icon("user"),
          h("span", { class: "nw" }, ""),
        );
        const stampLayer = h("div", { class: "stamp-layer", "aria-live": "polite" });
        const showStamp = (name: string, id: StampId) => {
          const el = h("p", { class: "stamp-pop ph" }, t("online.stampFrom", { name, stamp: stampText(id) }));
          stampLayer.append(el);
          setTimeout(() => el.remove(), 3500);
        };
        const renderRoom = (players: RoomPlayer[], me: number) => {
          roomPlayers = players;
          roomMe = me;
          const inRoom = me >= 0;
          stampBtn.hidden = !inRoom;
          playersBtn.hidden = !inRoom;
          if (!inRoom) stampPanel.hidden = true;
          playersBtn.lastElementChild!.textContent = String(players.filter((p) => p.connected).length);
          playersBtn.setAttribute("aria-label", t("online.players"));
        };
        const stopPlaying = () => {
          bridge?.destroy();
          bridge = null;
          relabel = null;
          if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
          document.body.classList.remove("playing");
          document.title = pageTitle();
          main.replaceChildren(...preMain());
        };
        // PC の「もどる」：遊ぶ前の画面が無いので、来たページ（ホームなど）へ戻る。外から直に開いたときはホームへ。
        // 手本の CrazyGames には止めるボタンが無く、ゲームを離れるのはほかのページへ移るとき（それに寄せる）
        const leave = () => {
          if ((history.state as { inSite?: boolean } | null)?.inSite) history.back();
          else navigate(localePath("/"));
        };
        let savedTimer = 0;
        const backLabel = h("span", { class: "pb-l" }, t("play.back"));
        const back = h("button", { class: "pb-btn", type: "button", "aria-label": t("play.back"), "data-testid": "play-back", onclick: inFrame ? leave : stopPlaying }, icon("back"), backLabel);
        // 遊んでいる最中の帯の題名（ページの見出しは下の説明の欄の題名なので、見出しにしない）
        const barTitle = h("span", { class: "gbar-t ph" }, pick(game!.title));
        // 説明と遊び方（Info）：PC は遊ぶ前の帯が無いので、遊んでいる最中の帯に置く
        const info =
          inFrame &&
          h("button", { class: "ib", type: "button", "aria-label": t("play.info"), title: t("play.info"), "aria-haspopup": "dialog", "data-testid": "info-button", onclick: () => openInfo(game!) }, icon("info"));
        // iPhone の Safari は要素の全画面ができない（押しても何も起きない）ので出さない。スマホの画面いっぱいの表示は CSS でしている
        const full =
          document.fullscreenEnabled &&
          h("button", { class: "ib", type: "button", "aria-label": t("play.fullscreen"), title: t("play.fullscreen"), onclick: () => pframe.requestFullscreen?.().catch(() => undefined) }, icon("full"));
        // 枠の下の帯：もどる・小さな絵と題名・保存の知らせ、右にスタンプ・部屋の人・音・全画面（スマホでは画面の上の細い帯になる）
        const bar = h(
          "div",
          { class: "gbar gbar-play" },
          back,
          h("img", { class: "gbar-ic", src: game!.thumbnail, alt: "", width: 512, height: 512 }),
          barTitle,
          saved,
          h("span", { class: "gbar-r" }, stampBtn, playersBtn, info, sound.el, full),
        );
        main.replaceChildren(crumb(), pframe, bar);
        // スタンプの一覧と届いたスタンプは、ゲームの枠の上に重ねる（ゲームを押し下げない）
        pframe.append(stampLayer, stampPanel);
        // 枠と枠の下の帯が画面に収まるように、ページの上から見せる
        if (window.scrollY > 0) window.scrollTo(0, 0);
        // （音：ゲームは最初の操作まで音を鳴らさない決まり。最初のタップは iframe の中への操作なので、そこでゲームが音を出せる）
        // 帯の「全画面」から始めたときは、押した流れのまま全画面にする（押した操作の中でないと全画面にできない）
        if (fullscreen) pframe.requestFullscreen?.().catch(() => undefined);

        relabel = () => {
          frame.title = pick(game!.title);
          barTitle.textContent = pick(game!.title);
          main.querySelector(".crumb")?.replaceWith(crumb());
          back.setAttribute("aria-label", t("play.back"));
          backLabel.textContent = t("play.back");
          if (full) {
            full.setAttribute("aria-label", t("play.fullscreen"));
            full.title = t("play.fullscreen");
          }
          if (info) {
            info.setAttribute("aria-label", t("play.info"));
            info.title = t("play.info");
          }
          sound.render();
          stampBtn.textContent = t("online.stamp");
          STAMPS.forEach((id, i) => (stampBtns[i]!.textContent = stampText(id)));
          playersBtn.setAttribute("aria-label", t("online.players"));
        };

        bridge = new GameBridge(
          frame,
          game!,
          {
            requestSignIn: () => openSignIn(),
            onLoadingFinished: () => loading.done(),
            onSaved: (cloud) => {
              saved.replaceChildren(icon("check"), t(cloud ? "saved.cloudDone" : "saved.deviceDone"));
              saved.hidden = false;
              clearTimeout(savedTimer);
              savedTimer = window.setTimeout(() => (saved.hidden = true), 3000);
            },
            showResult: (params, submit, board) => showResult(pframe, params, submit, board),
            onRoster: renderRoom,
            onStamp: (from, id) => showStamp(from?.displayName ?? "", id),
            onSuspended: openSuspended,
          },
          config.playOrigin,
        );
      }

      // ── 終わった後 ──
      function showResult(pframe: HTMLElement, params: ResultParams, submit: SubmitResult | null, board: CatalogBoard | null): Promise<void> {
        return new Promise((resolve) => {
          const signedIn = !!session().user;
          const improved = submit?.status === "submitted" ? submit.improved : board ? updateLocalBest(game!.id, board.id, params.score, board.order) : false;
          const period: Period = board?.periods[0] ?? "all";
          const rankText = submit?.status === "submitted" && submit.rank ? `${t(period === "daily" ? "result.today" : "result.allTime")} ${t("rank.place", { n: submit.rank })}` : "";
          const list = h("ol", { class: "rk-list", "data-testid": "result-ranking" });
          const meRow = h("p", { class: "rk-me", hidden: true });
          const box = h("div", { class: "result", "data-testid": "result" });
          // ログインの窓が閉じても、呼び名の作成が終わるまではログイン済みと判定できない。
          // ログインの状態が変わった時点で、促しを消す。
          const offSignedIn = onSession((s) => {
            if (s.user) nudge?.remove();
          });
          const close = () => {
            offSignedIn();
            box.remove();
            adBar.remove();
            pframe.classList.remove("dim");
          };
          const retry = h(
            "button",
            {
              class: "btn-play sm",
              type: "button",
              "data-testid": "result-retry",
              onclick: () => {
                close();
                resolve();
              },
            },
            t("result.retry"),
          );
          const nudge =
            !signedIn && sessionStorage.getItem(NUDGE_KEY) !== "1"
              ? h(
                  "div",
                  { class: "nudge" },
                  h("p", { class: "ph" }, t("result.nudge")),
                  h(
                    "p",
                    { class: "nudge-b" },
                    h(
                      "button",
                      {
                        class: "btn-login",
                        type: "button",
                        onclick: () => void openSignIn(),
                      },
                      t("result.signIn"),
                    ),
                    h(
                      "button",
                      {
                        class: "btn-text",
                        type: "button",
                        onclick: () => {
                          // 促しは1回だけ。「あとで」を押したら、この訪問のあいだは出さない
                          sessionStorage.setItem(NUDGE_KEY, "1");
                          nudge?.remove();
                        },
                      },
                      t("result.later"),
                    ),
                  ),
                )
              : null;
          box.append(
            h(
              "div",
              { class: "rs-main" },
              h("p", { class: "rs-k ph", "data-testid": "result-message" }, params.message || t("result.done")),
              h("p", { class: "rs-score" }, h("span", { class: "nw", "data-testid": "result-score" }, fmt(params.score)), h("small", {}, t("result.points"))),
              h("p", { class: "rs-best" }, improved && h("span", { class: "badge" }, t("result.best")), rankText && h("span", { "data-testid": "result-rank" }, rankText), submit?.status === "offline" && h("span", { class: "small" }, t("result.offline"))),
              h("div", { class: "rs-btns" }, retry, next && h("a", { class: "btn-sub rs-next", href: gamePath(next) }, h("small", {}, t("result.nextGame")), h("span", { class: "ph" }, pick(next.title)))),
              nudge,
            ),
          );
          if (board) {
            box.append(h("div", { class: "rk-box" }, h("h2", { class: "h3" }, icon("trophy"), t(period === "daily" ? "rank.daily" : "rank.all")), list, meRow));
            leaderboard
              .top(game!.id, board, period, 10)
              .then((entries) => {
                list.replaceChildren(...(entries.length ? rankRows(entries) : [h("li", { class: "empty small" }, t("rank.empty"))]));
                if (!signedIn) {
                  // ゲストは、いまの点が何位に当たるかだけを見せる（ランキングには載らない）
                  const better = (a: number, b: number) => (board.order === "desc" ? a > b : a < b);
                  const ahead = entries.filter((e) => better(e.score, params.score)).length;
                  meRow.replaceChildren(
                    h("span", { class: "rk-n" }, entries.length < 10 || ahead < 10 ? String(ahead + 1) : "–"),
                    h("span", { class: "rk-name" }, t("rank.guest")),
                    h("span", { class: "rk-s nw" }, fmt(params.score)),
                  );
                  meRow.hidden = false;
                }
              })
              .catch((e) => {
                console.error(e);
                list.replaceChildren(h("li", { class: "empty small" }, t("rank.error")));
              });
          }
          // 広告の枠は、終わった後の画面の下にも置く（PC は枠の下の帯のさらに下。スマホは画面いっぱいの枠の下）
          const adBar = adSlot("play", "ad-dark");
          pframe.classList.add("dim");
          pframe.append(box);
          pframe.parentElement?.append(adBar);
          retry.focus();
        });
      }

      const offSession = onSession((s) => {
        const uid = s.user?.uid ?? null;
        if (uid !== lastUid) {
          lastUid = uid;
          resetSaves();
        }
        bridge?.send("user", userInfo());
      });

      onLocale = () => {
        if (bridge) {
          // 遊んでいる最中は読み直さず、ゲームに言語を知らせる。帯の文言と右の列は付け直す
          document.title = pageTitle();
          bridge.send("locale", { locale: getLocale() });
          relabel?.();
          const fresh = renderSide();
          side.replaceWith(fresh);
          side = fresh;
          const freshAbout = aboutSection(game);
          about.replaceWith(freshAbout);
          about = freshAbout;
        } else {
          renderPage();
        }
      };
      cleanup = [
        () => bridge?.destroy(),
        () => ranking.destroy(),
        offSession,
        () => document.body.classList.remove("playing"),
      ];
      renderPage();
    })
    .catch((e) => {
      console.error(e);
      el.replaceChildren(h("p", { class: "wrap empty" }, t("error.generic")));
    });

  return { el, destroy: () => cleanup.forEach((f) => f()), onLocale: () => onLocale?.() };
}
